"use client";

import { forwardRef } from "react";

import { StoreImage } from "~/components/ui/store-image";
import type { SwatchConfig } from "~/config/storefront";
import { swatchBackground } from "~/lib/colour-swatch";
import { cn } from "~/lib/utils";
import type { PdpOptionValue } from "~/server/api/schemas/pdp";

/** Visible swatch diameter per `swatch.size`; the hit area stays ≥44px. */
const SIZE_CLASS: Record<SwatchConfig["size"], string> = {
  sm: "size-6",
  md: "size-8",
  lg: "size-10",
};

const SHAPE_CLASS: Record<SwatchConfig["shape"], string> = {
  circle: "rounded-full",
  square: "rounded-[var(--radius-md)]",
};

function CheckIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}

export interface SwatchProps {
  value: PdpOptionValue;
  selected: boolean;
  available: boolean;
  selectable?: boolean;
  selectionHint?: string;
  config: SwatchConfig;
  /** Roving-tabindex owner: 0 for the active swatch, -1 for the rest. */
  tabIndex: number;
  onSelect: () => void;
  /** Fired on pointer/focus enter so the group can label the colour. */
  onHover: () => void;
}

/**
 * One colour swatch (P3-05): solid (`swatchHex`), image (colour thumbnail), or
 * a labelled text fallback when the value carries neither — so a colour is
 * never invisible. Rendered as an ARIA `radio`; selection/keyboard live on the
 * parent {@link SwatchGroup}. Unavailable swatches are `aria-disabled` (dimmed
 * or struck per config) and inert, not hidden, to avoid a layout jump.
 */
export const Swatch = forwardRef<HTMLButtonElement, SwatchProps>(
  function Swatch(
    { value, selected, available, selectable = available, selectionHint, config, tabIndex, onSelect, onHover },
    ref,
  ) {
    const shape = SHAPE_CLASS[config.shape];
    const struck = !available && config.unavailableStyle === "strike";
    const dimmed = !available && config.unavailableStyle === "dim";
    // The name paints the swatch when the value has no hex of its own.
    const fill = swatchBackground(value.value, value.swatchHex);
    const hasVisual = fill !== null || value.swatchImageUrl !== null;

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={selected}
        aria-disabled={!selectable || undefined}
        aria-label={selectionHint ? `${value.value} — ${selectionHint}` : available ? value.value : `${value.value} — ${selectable ? "out of stock; select to view availability alerts" : "no variant exists"}`}
        title={value.value}
        tabIndex={tabIndex}
        data-testid={`swatch-${value.id}`}
        data-selected={selected || undefined}
        data-available={available}
        onMouseEnter={onHover}
        onFocus={onHover}
        onClick={() => {
          if (selectable) onSelect();
        }}
        className={cn(
          // ≥44px touch target regardless of the visible swatch size.
          "relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-md)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          !selectable && "cursor-not-allowed",
        )}
      >
        <span
          className={cn(
            "relative flex items-center justify-center overflow-hidden border transition-shadow",
            SIZE_CLASS[config.size],
            shape,
            selected
              ? "border-fg ring-2 ring-fg ring-offset-2 ring-offset-surface"
              : "border-border",
            dimmed && "opacity-40",
          )}
          style={fill ? { background: fill } : undefined}
        >
          {value.swatchImageUrl ? (
            <StoreImage
              src={value.swatchImageUrl}
              alt=""
              fill
              preset="thumb"
              loading="lazy"
              className="object-cover"
            />
          ) : !hasVisual ? (
            // Text fallback: no hex and no image — keep the colour legible.
            <span className="px-0.5 text-[0.6rem] font-medium uppercase text-fg-muted">
              {value.value.slice(0, 3)}
            </span>
          ) : null}

          {selected && (
            <span
              aria-hidden
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="flex size-4 items-center justify-center rounded-full bg-white/90 text-black shadow-sm">
                <CheckIcon />
              </span>
            </span>
          )}

          {struck && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-70"
            >
              <span className="h-px w-[141%] rotate-45 bg-fg" />
            </span>
          )}
        </span>
      </button>
    );
  },
);
